import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import type { NetSuiteClass, NetSuiteProductEntity } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { isEmptyString, isNullOrUndefined } from '@vy/utils';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNetSuiteClasses } from '../../hooks/requests/integrations';
import { useNetSuiteProducts } from '../../hooks/requests/integrations/net-suite/use-net-suite-products';
import { useUpdateProductIntegrationEntities } from '../../hooks/requests/use-update-product-integration-entities';
import { createNetSuiteIntegrationEntity } from '../../utils/integration-entity-creators';
import type { FormattedIntegrationProduct } from '../../utils/types/formatted-integration-product';
import { VyDialog } from '../new-design/feedback/VyDialog';
import { integrationAutocompleteSx, integrationFieldLabelSx } from './integration-field-styles';

type AddErpIdDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  productId: string;
  customerId?: string;
};

export const AddErpIdDialog: FC<AddErpIdDialogProps> = ({ open, onClose, onSave, productId, customerId }) => {
  const [selectedProduct, setSelectedProduct] = useState<FormattedIntegrationProduct | null>(null);
  const [selectedClass, setSelectedClass] = useState<NetSuiteClass | null>(null);

  const { data: { products } = {} } = useNetSuiteProducts(customerId);
  const netSuiteClasses = useNetSuiteClasses();
  const updateProductIntegrationEntities = useUpdateProductIntegrationEntities();

  const formattedProducts = useMemo((): FormattedIntegrationProduct[] => {
    if (isNullOrUndefined(products) || products.length === 0) {
      return [];
    }

    return products.map(({ id, name }) => ({ id, name }));
  }, [products]);

  const handleProductChange = useCallback((_: unknown, value: FormattedIntegrationProduct | null) => {
    setSelectedProduct(value);
  }, []);

  const handleClassChange = useCallback((_: unknown, value: NetSuiteClass | null) => {
    setSelectedClass(value);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedProduct(null);
    setSelectedClass(null);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (isNullOrUndefined(selectedProduct)) {
      return;
    }

    const baseEntity = createNetSuiteIntegrationEntity(selectedProduct);

    if (isNullOrUndefined(baseEntity)) {
      return;
    }

    const integrationEntity: NetSuiteProductEntity = {
      ...baseEntity,
      integrationProvider: INTEGRATION_PROVIDERS.netSuite,
      metadata: {
        ...baseEntity.metadata,
        classMetadata: selectedClass ?? undefined,
      },
    };

    await updateProductIntegrationEntities.mutateAsync({
      productId,
      integrationEntities: [integrationEntity],
    });

    setSelectedProduct(null);
    setSelectedClass(null);
    onSave();
  }, [selectedProduct, selectedClass, productId, updateProductIntegrationEntities, onSave]);

  const isSaveDisabled = isNullOrUndefined(selectedProduct) || updateProductIntegrationEntities.isLoading;

  return (
    <VyDialog
      open={open}
      onClose={handleClose}
      label="Add ERP ID"
      labelDescription="Link to your internal ERP system for tracking and reporting"
      cancelButtonText="Cancel"
      ctaText="Save"
      ctaDisabled={isSaveDisabled}
      onSubmit={handleSave}
      maxWidth="sm"
      disableDividers
      dialogContentProps={{
        style: { minWidth: '580px', paddingTop: '30px' },
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography sx={integrationFieldLabelSx}>NetSuite Products</Typography>
          <Autocomplete
            options={formattedProducts}
            value={selectedProduct}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleProductChange}
            sx={integrationAutocompleteSx}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select NetSuite product"
                onChange={(event) => {
                  if (isEmptyString(event.target.value)) {
                    setSelectedProduct(null);
                  }
                }}
              />
            )}
          />
        </Box>

        <Box>
          <Typography sx={integrationFieldLabelSx}>NetSuite Class</Typography>
          <Autocomplete
            options={netSuiteClasses}
            value={selectedClass}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleClassChange}
            disabled={isNullOrUndefined(selectedProduct)}
            sx={integrationAutocompleteSx}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select NetSuite class"
                variant="outlined"
                onChange={(event) => {
                  if (isEmptyString(event.target.value)) {
                    setSelectedClass(null);
                  }
                }}
              />
            )}
          />
        </Box>
      </Stack>
    </VyDialog>
  );
};
